import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaSearch,
  FaUserPlus,
  FaSync,
  FaExclamationTriangle,
  FaTimes,
  FaPencilAlt,
  FaTrashAlt,
  FaPlus,
  FaBuilding,
  FaUser,
  FaCheck,
  FaUsers,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

import {
  getCustomers,
  getSitesForCustomer,
  createSite,
  updateSite,
  deleteSite,
  updateCustomer,
  deleteCustomer
} from '../../services/customerService';

import SiteForm from './SiteForm';
import ContactDisplay from './ContactDisplay';
import './CustomerList.css';

const normalizeStr = (v) => String(v || '').toLowerCase().trim();
const includesQ = (value, q) => normalizeStr(value).includes(q);

const matchesContacts = (contacts = [], q) =>
  contacts.some((c) => {
    if (includesQ(c?.name, q)) return true;
    if (includesQ(c?.email, q)) return true;
    const phones = c?.phone_numbers || [];
    return phones.some((p) => includesQ(p?.number, q));
  });

const CustomerList = ({
  onAddNewClick,
  selectionMode = false,
  onSelectSite = null,
  refreshTrigger
}) => {
  // Data state
  const [customers, setCustomers] = useState([]);
  const [sitesByCustomer, setSitesByCustomer] = useState({});

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCustomerIds, setExpandedCustomerIds] = useState([]);
  const [expandedSiteIds, setExpandedSiteIds] = useState([]);

  // Loading/error state
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [refreshingCustomers, setRefreshingCustomers] = useState(false);
  const [error, setError] = useState(null);

  const [siteLoadingByCustomer, setSiteLoadingByCustomer] = useState({});
  const [siteErrorByCustomer, setSiteErrorByCustomer] = useState({});

  // Edit state
  const [siteEditStateByCustomer, setSiteEditStateByCustomer] = useState({});
  const [customerEditState, setCustomerEditState] = useState({});

  // Deleting state
  const [deletingSiteId, setDeletingSiteId] = useState(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);

  const loadCustomers = useCallback(async ({ showLoader } = { showLoader: true }) => {
    try {
      if (showLoader) setLoadingCustomers(true);
      setRefreshingCustomers(true);
      setError(null);

      // Load all customers with their sites to enable full search
      const data = await getCustomers({ per_page: 2000, includeSites: true });
      const customerList = Array.isArray(data) ? data : [];
      setCustomers(customerList);

      // Populate sitesByCustomer with the loaded sites for search functionality
      const sitesMap = {};
      customerList.forEach((customer) => {
        if (customer.sites && Array.isArray(customer.sites)) {
          sitesMap[customer.id] = customer.sites;
        }
      });
      setSitesByCustomer(sitesMap);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers.');
    } finally {
      setLoadingCustomers(false);
      setRefreshingCustomers(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers({ showLoader: true });
  }, [loadCustomers, refreshTrigger]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const filteredCustomers = useMemo(() => {
    const q = normalizeStr(searchTerm);
    if (!q) return customers;

    return customers.filter((customer) => {
      if (includesQ(customer?.name, q)) return true;
      if (includesQ(customer?.company_name, q)) return true;
      if (includesQ(customer?.contact_name, q)) return true;
      if (includesQ(customer?.email, q)) return true;

      if (matchesContacts(customer?.contacts || [], q)) return true;

      const sites = sitesByCustomer[customer.id] || [];
      return sites.some((site) => {
        if (includesQ(site?.name, q)) return true;
        if (includesQ(site?.address, q)) return true;
        if (matchesContacts(site?.contacts || [], q)) return true;
        return false;
      });
    });
  }, [customers, searchTerm, sitesByCustomer]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE));
  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCustomers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCustomers, currentPage]);

  const ensureSitesLoaded = useCallback(
    async (customerId, { force = false } = {}) => {
      if (!force && Array.isArray(sitesByCustomer[customerId])) return;
      if (siteLoadingByCustomer[customerId]) return;

      setSiteLoadingByCustomer((prev) => ({ ...prev, [customerId]: true }));
      setSiteErrorByCustomer((prev) => ({ ...prev, [customerId]: null }));

      try {
        const sites = await getSitesForCustomer(customerId);
        setSitesByCustomer((prev) => ({
          ...prev,
          [customerId]: Array.isArray(sites) ? sites : []
        }));
      } catch (err) {
        console.error(`Error loading sites for customer ${customerId}:`, err);
        setSiteErrorByCustomer((prev) => ({
          ...prev,
          [customerId]: 'Failed to load sites.'
        }));
      } finally {
        setSiteLoadingByCustomer((prev) => ({ ...prev, [customerId]: false }));
      }
    },
    [sitesByCustomer, siteLoadingByCustomer]
  );

  const handleManualRefresh = useCallback(() => {
    setSearchTerm('');
    setExpandedCustomerIds([]);
    setExpandedSiteIds([]);
    setSitesByCustomer({});
    setSiteEditStateByCustomer({});
    setCustomerEditState({});
    loadCustomers({ showLoader: false });
  }, [loadCustomers]);

  const toggleExpandCustomer = useCallback(
    async (customerId) => {
      setExpandedCustomerIds((prev) => {
        const isExpanded = prev.includes(customerId);
        return isExpanded ? prev.filter((id) => id !== customerId) : [...prev, customerId];
      });

      // Load sites on expand (best effort)
      if (!expandedCustomerIds.includes(customerId)) {
        await ensureSitesLoaded(customerId);
      }
    },
    [ensureSitesLoaded, expandedCustomerIds]
  );

  const toggleExpandSite = useCallback((siteId) => {
    setExpandedSiteIds((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]
    );
  }, []);

  // ----- Customer edit -----
  const beginEditCustomer = useCallback((customer) => {
    setCustomerEditState((prev) => ({
      ...prev,
      [customer.id]: {
        editing: true,
        name: customer.name || customer.company_name || '',
        error: null
      }
    }));
  }, []);

  const cancelEditCustomer = useCallback((customerId) => {
    setCustomerEditState((prev) => {
      const next = { ...prev };
      delete next[customerId];
      return next;
    });
  }, []);

  const saveCustomerName = useCallback(
    async (customer) => {
      const state = customerEditState[customer.id];
      if (!state?.editing) return;

      const nextName = (state.name || '').trim();
      if (!nextName) {
        setCustomerEditState((prev) => ({
          ...prev,
          [customer.id]: { ...prev[customer.id], error: 'Name is required.' }
        }));
        return;
      }

      try {
        const updated = await updateCustomer(customer.id, { name: nextName });
        setCustomers((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
        cancelEditCustomer(customer.id);
      } catch (err) {
        console.error('Failed to update customer:', err);
        setCustomerEditState((prev) => ({
          ...prev,
          [customer.id]: { ...prev[customer.id], error: 'Update failed.' }
        }));
      }
    },
    [customerEditState, cancelEditCustomer]
  );

  const handleDeleteCustomer = useCallback(
    async (customerId) => {
      if (!window.confirm('Delete customer and ALL data?')) return;

      setDeletingCustomerId(customerId);
      try {
        await deleteCustomer(customerId);
        setCustomers((prev) => prev.filter((c) => c.id !== customerId));

        setSitesByCustomer((prev) => {
          const next = { ...prev };
          delete next[customerId];
          return next;
        });
        setExpandedCustomerIds((prev) => prev.filter((id) => id !== customerId));
        setSiteEditStateByCustomer((prev) => {
          const next = { ...prev };
          delete next[customerId];
          return next;
        });
        cancelEditCustomer(customerId);
      } catch (err) {
        console.error('Failed to delete customer:', err);
        setError(`Failed to delete customer ID ${customerId}.`);
      } finally {
        setDeletingCustomerId(null);
      }
    },
    [cancelEditCustomer]
  );

  // ----- Site CRUD -----
  const beginAddSite = useCallback(
    (customerId) => {
      setSiteEditStateByCustomer((prev) => ({
        ...prev,
        [customerId]: { mode: 'add', site: null }
      }));

      if (!expandedCustomerIds.includes(customerId)) {
        toggleExpandCustomer(customerId);
      }
    },
    [expandedCustomerIds, toggleExpandCustomer]
  );

  const beginEditSite = useCallback(
    (customerId, site) => {
      setSiteEditStateByCustomer((prev) => ({
        ...prev,
        [customerId]: { mode: 'edit', site }
      }));

      if (!expandedCustomerIds.includes(customerId)) {
        toggleExpandCustomer(customerId);
      }
    },
    [expandedCustomerIds, toggleExpandCustomer]
  );

  const cancelSiteEdit = useCallback((customerId) => {
    setSiteEditStateByCustomer((prev) => {
      const next = { ...prev };
      delete next[customerId];
      return next;
    });
    setSiteErrorByCustomer((prev) => ({ ...prev, [customerId]: null }));
  }, []);

  const handleSiteFormSubmit = useCallback((customerId, savedSite) => {
    const editState = siteEditStateByCustomer[customerId];
    if (!editState) return;

    // Update local state with the saved site
    if (editState.mode === 'edit') {
      setSitesByCustomer((prev) => ({
        ...prev,
        [customerId]: (prev[customerId] || []).map((s) => (s.id === savedSite.id ? savedSite : s))
      }));
    } else {
      setSitesByCustomer((prev) => ({
        ...prev,
        [customerId]: [...(prev[customerId] || []), savedSite]
      }));
    }

    cancelSiteEdit(customerId);
  }, [siteEditStateByCustomer, cancelSiteEdit]);

  const saveSite = useCallback(
    async (customerId, siteData) => {
      const editState = siteEditStateByCustomer[customerId];
      if (!editState) return;

      setSiteErrorByCustomer((prev) => ({ ...prev, [customerId]: null }));

      try {
        if (editState.mode === 'edit' && editState.site?.id) {
          const updated = await updateSite(editState.site.id, siteData);
          setSitesByCustomer((prev) => ({
            ...prev,
            [customerId]: (prev[customerId] || []).map((s) => (s.id === updated.id ? updated : s))
          }));
        } else {
          const created = await createSite(customerId, siteData);
          setSitesByCustomer((prev) => ({
            ...prev,
            [customerId]: [...(prev[customerId] || []), created]
          }));
        }

        cancelSiteEdit(customerId);
      } catch (err) {
        console.error('Failed to save site:', err);
        setSiteErrorByCustomer((prev) => ({ ...prev, [customerId]: 'Failed to save site.' }));
      }
    },
    [siteEditStateByCustomer, cancelSiteEdit]
  );

  const removeSite = useCallback(async (customerId, siteId) => {
    if (!window.confirm('Are you sure you want to delete this site?')) return;

    setDeletingSiteId(siteId);
    try {
      await deleteSite(siteId);
      setSitesByCustomer((prev) => ({
        ...prev,
        [customerId]: (prev[customerId] || []).filter((s) => s.id !== siteId)
      }));
      setExpandedSiteIds((prev) => prev.filter((id) => id !== siteId));
    } catch (err) {
      console.error('Failed to delete site:', err);
      setSiteErrorByCustomer((prev) => ({ ...prev, [customerId]: 'Failed to delete site.' }));
    } finally {
      setDeletingSiteId(null);
    }
  }, []);

  const handleSelectSite = useCallback(
    (customer, site) => {
      if (selectionMode && onSelectSite) onSelectSite(customer, site);
    },
    [selectionMode, onSelectSite]
  );

  // ----- “add new customer” callback wiring -----
  const handleCustomerSaved = useCallback(
    (savedCustomer) => {
      if (!savedCustomer?.id) {
        loadCustomers({ showLoader: false });
        return;
      }

      setCustomers((prev) => {
        const idx = prev.findIndex((c) => c.id === savedCustomer.id);
        if (idx === -1) return [savedCustomer, ...prev];
        const next = [...prev];
        next[idx] = { ...next[idx], ...savedCustomer };
        return next;
      });

      setTimeout(() => loadCustomers({ showLoader: false }), 200);
    },
    [loadCustomers]
  );

  // ----- Early returns -----
  if (loadingCustomers && customers.length === 0) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading customers...</p>
      </div>
    );
  }

  if (error && customers.length === 0) {
    return (
      <div className="alert alert-danger text-center m-3" role="alert">
        <h4 className="alert-heading">Error</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={handleManualRefresh}>
          <FaSync /> Try Again
        </button>
      </div>
    );
  }

  if (!loadingCustomers && customers.length === 0) {
    return (
      <div className="text-center p-5">
        <FaUser className="display-4 text-muted mb-3" />
        <h4>No Customers Found</h4>
        {onAddNewClick && (
          <button className="btn btn-success mt-3" onClick={() => onAddNewClick(handleCustomerSaved)}>
            <FaUserPlus /> Add First Customer
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="customer-list-container">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <FaExclamationTriangle /> {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      {/* Toolbar */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
          <form className="flex-grow-1 me-sm-2" onSubmit={(e) => e.preventDefault()}>
            <div className="input-group">
              <span className="search-icon-wrapper">
                <FaSearch />
              </span>
              <input
                type="search"
                className="form-control"
                placeholder="Search Customers or Sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchTerm('')}
                  title="Clear Search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </form>

          <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-sm-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleManualRefresh}
              disabled={refreshingCustomers}
              title="Refresh List"
            >
              <FaSync /> Refresh
            </button>

            {onAddNewClick && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onAddNewClick(handleCustomerSaved)}
                title="Add New Customer"
              >
                <FaUserPlus /> Add Customer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover customer-table align-middle">
          <thead className="customer-list__thead">
            <tr>
              <th>Customer Name</th>
              <th className="text-center">Sites</th>
              <th className="action-column text-end">Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center p-4">
                  <FaSearch className="display-5 text-muted mb-3" />
                  <h5>No Customers Match Your Search</h5>
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer) => {
                const isExpanded = expandedCustomerIds.includes(customer.id);
                const isDeletingCustomer = deletingCustomerId === customer.id;

                const sites = sitesByCustomer[customer.id];
                const sitesLoading = !!siteLoadingByCustomer[customer.id];
                const sitesError = siteErrorByCustomer[customer.id];

                const editingCustomer = !!customerEditState[customer.id]?.editing;
                const editingSite = siteEditStateByCustomer[customer.id];

                return (
                  <React.Fragment key={customer.id}>
                    <tr
                      onClick={!editingCustomer ? () => toggleExpandCustomer(customer.id) : undefined}
                      className={`customer-row ${isExpanded ? 'expanded-row' : ''} ${editingCustomer ? 'is-editing' : ''}`}
                      style={{ cursor: editingCustomer ? 'default' : 'pointer' }}
                    >
                      <td data-label="Name">
                        {editingCustomer ? (
                          <div className="input-group input-group-sm" onClick={(e) => e.stopPropagation()}>
                            <input
                              className={`form-control ${customerEditState[customer.id]?.error ? 'is-invalid' : ''}`}
                              value={customerEditState[customer.id]?.name ?? ''}
                              onChange={(e) =>
                                setCustomerEditState((prev) => ({
                                  ...prev,
                                  [customer.id]: { ...prev[customer.id], name: e.target.value, error: null }
                                }))
                              }
                              autoFocus
                            />
                            <button
                              type="button"
                              className="btn btn-outline-success"
                              onClick={() => saveCustomerName(customer)}
                              title="Save"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => cancelEditCustomer(customer.id)}
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: customer.company_name || customer.name ? 'inherit' : '#999' }}>
                            {customer.company_name || customer.name || '(Unnamed Customer)'}
                          </span>
                        )}

                        {isDeletingCustomer && (
                          <div className="spinner-border spinner-border-sm text-danger ms-2" role="status">
                            <span className="visually-hidden">Deleting...</span>
                          </div>
                        )}
                      </td>

                      <td data-label="Sites" className="text-center">
                        {sitesLoading ? (
                          <div className="spinner-border spinner-border-sm text-secondary" role="status">
                            <span className="visually-hidden">Loading sites...</span>
                          </div>
                        ) : (
                          customer.site_count ?? 0
                        )}
                        {sitesError && <FaExclamationTriangle className="text-danger ms-1" title={sitesError} />}
                      </td>

                      <td data-label="Action" className="action-column text-end">
                        <div className="action-stack">
                          {!editingCustomer && (
                            <button
                              type="button"
                              className="btn btn-info"
                              disabled={isDeletingCustomer}
                              onClick={(e) => {
                                e.stopPropagation();
                                beginEditCustomer(customer);
                              }}
                              style={{
                                backgroundColor: '#14b8a6',
                                borderColor: '#14b8a6',
                                color: 'white'
                              }}
                            >
                              <FaPencilAlt /> Edit
                            </button>
                          )}

                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            disabled={isDeletingCustomer || editingCustomer}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.id);
                            }}
                          >
                            <FaTrashAlt /> Delete
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={isDeletingCustomer || editingCustomer}
                            onClick={(e) => {
                              e.stopPropagation();
                              beginAddSite(customer.id);
                            }}
                          >
                            <FaPlus /> Add Site
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="expanded-details-row">
                        <td colSpan={3} className="p-0">
                          <div className="nested-content customer-list__expanded p-3">
                            {sitesLoading && (
                              <div className="text-center my-3">
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            )}

                            {sitesError && !editingSite && <div className="alert alert-danger">{sitesError}</div>}

                            {!editingSite && (
                              <div className="customer-details-panel mb-4">
                                <h5 className="mb-3">
                                  <FaBuilding className="me-2" />
                                  Company Information
                                </h5>

                                <div className="customer-details-card mb-3">
                                  <div className="row">
                                    <div className="col-md-6">
                                      <div>
                                        <strong>Company Name:</strong> {customer.company_name || customer.name}
                                      </div>
                                      {customer.address && (
                                        <div>
                                          <strong>Address:</strong> {customer.address}
                                        </div>
                                      )}
                                    </div>
                                    <div className="col-md-6">
                                      <div>
                                        <strong>Created:</strong>{' '}
                                        {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <ContactDisplay contacts={customer.contacts || []} title="Company Contacts" />
                              </div>
                            )}

                            {editingSite && (
                              <div className="card mb-3">
                                <div className="card-header">
                                  <h6 className="mb-0">{editingSite.mode === 'edit' ? 'Edit Site' : 'Add Site'}</h6>
                                </div>
                                <div className="card-body">
                                  <SiteForm
                                    customerId={customer.id}
                                    siteToEdit={editingSite.site}
                                    onFormSubmit={(savedSite) => handleSiteFormSubmit(customer.id, savedSite)}
                                    onCancel={() => cancelSiteEdit(customer.id)}
                                  />
                                </div>
                              </div>
                            )}

                            {Array.isArray(sites) && !editingSite && (
                              <>
                                {sites.length > 0 ? (
                                  <div>
                                    <h5 className="mb-3">
                                      <FaBuilding className="me-2" />
                                      Sites ({sites.length})
                                    </h5>

                                    <div className="table-responsive">
                                      <table className="table table-sm table-bordered nested-table">
                                        <thead>
                                          <tr>
                                            <th>Site Name</th>
                                            <th>Address</th>
                                            <th>Contacts</th>
                                            <th className="action-column text-end">Actions</th>
                                          </tr>
                                        </thead>

                                        <tbody>
                                          {sites.map((site) => {
                                            const isSiteExpanded = expandedSiteIds.includes(site.id);
                                            const isDeletingSite = deletingSiteId === site.id;

                                            return (
                                              <React.Fragment key={site.id}>
                                                <tr
                                                  onClick={() => toggleExpandSite(site.id)}
                                                  className="site-row"
                                                  style={{ cursor: 'pointer' }}
                                                >
                                                  <td>
                                                    <div className="d-flex align-items-center">
                                                      {isSiteExpanded ? '▼' : '▶'}
                                                      <span className="ms-2">{site.name || '-'}</span>
                                                    </div>
                                                  </td>

                                                  <td>{site.address || '-'}</td>

                                                  <td>
                                                    {site.contacts?.length ? (
                                                      <div>
                                                        <FaUsers className="me-1" />
                                                        {site.contacts.length} contact{site.contacts.length !== 1 ? 's' : ''}
                                                      </div>
                                                    ) : (
                                                      <span className="text-muted">No contacts</span>
                                                    )}
                                                  </td>

                                                  <td className="action-column text-end">
                                                    <div className="action-stack">
                                                      <button
                                                        type="button"
                                                        className="btn btn-info"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          beginEditSite(customer.id, site);
                                                        }}
                                                        disabled={isDeletingSite}
                                                        title="Edit Site"
                                                        style={{
                                                          backgroundColor: '#14b8a6',
                                                          borderColor: '#14b8a6',
                                                          color: 'white'
                                                        }}
                                                      >
                                                        <FaPencilAlt /> Edit
                                                      </button>

                                                      <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          removeSite(customer.id, site.id);
                                                        }}
                                                        disabled={isDeletingSite}
                                                        title="Delete Site"
                                                      >
                                                        {isDeletingSite ? (
                                                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                        ) : (
                                                          <>
                                                            <FaTrashAlt /> Delete
                                                          </>
                                                        )}
                                                      </button>

                                                      {selectionMode && (
                                                        <button
                                                          type="button"
                                                          className="btn btn-success"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectSite(customer, site);
                                                          }}
                                                          title={`Select Site ${site.name}`}
                                                        >
                                                          <FaCheck /> Select
                                                        </button>
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>

                                                {isSiteExpanded && (
                                                  <tr className="site-details-row">
                                                    <td colSpan={4} className="p-3 customer-list__siteDetails">
                                                      <div className="site-details-content">
                                                        <div className="row mb-3">
                                                          <div className="col-md-6">
                                                            <strong>Site Information:</strong>
                                                            <div className="mt-2">
                                                              <div>
                                                                <strong>Name:</strong> {site.name || '-'}
                                                              </div>
                                                              <div>
                                                                <strong>Address:</strong> {site.address || '-'}
                                                              </div>
                                                              {site.lockbox_location && (
                                                                <div>
                                                                  <strong>Lockbox:</strong> {site.lockbox_location}
                                                                </div>
                                                              )}
                                                            </div>
                                                          </div>
                                                        </div>

                                                        <ContactDisplay contacts={site.contacts || []} title="Site Contacts" />
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center my-3">
                                    <p className="text-muted">No sites found.</p>
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => beginAddSite(customer.id)}
                                    >
                                      <FaPlus /> Add First Site
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3 px-2">
          <small className="text-muted">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredCustomers.length)} of {filteredCustomers.length} customers
          </small>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <FaChevronLeft /> Prev
            </button>
            <span className="mx-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
